import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateSerial } from "../../../components/serials/functions";
import { getProduct } from "../../../components/products/functions";
import ToastContext from "../../../context/toastProvider";
import {
  gradingTypeSupportsGeneralScore,
  gradingTypeSupportsSubgrades,
} from "../../../components/utils/gradingTypes";
import {
  getVisibleGradeFields,
  renderApplicableGradeValue,
} from "../../../components/utils/serialGradeFields";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha disabled:bg-monao";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao";
const dangerBtn =
  "inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-700 px-4 text-sm font-medium text-white hover:bg-red-800";

function SheetDetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <p className="text-sm text-laranjinha/80">
      <span className="text-laranjinha/60">{label}: </span>
      <span className="font-medium text-laranjinha">{value}</span>
    </p>
  );
}

function normalizeProductId(value) {
  return value?._id || value || "";
}

export function MobileSerialDetailSheet({ open, onClose, serial, user, onChanged }) {
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);

  const originalProductId = serial?.product?._id || serial?.product || "";
  const supportRequestIdRef = useRef(0);

  const [gradableModel, setGradableModel] = useState("");
  const [productId, setProductId] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [generalScore, setGeneralScore] = useState("");
  const [surface, setSurface] = useState("");
  const [edges, setEdges] = useState("");
  const [centering, setCentering] = useState("");
  const [corners, setCorners] = useState("");
  const [notaAutografo, setNotaAutografo] = useState("");
  const [enableChanges, setEnableChanges] = useState(false);
  const [supportsGeneralScore, setSupportsGeneralScore] = useState(true);
  const [supportsSubgrades, setSupportsSubgrades] = useState(true);
  const [supportsAutographNote, setSupportsAutographNote] = useState(false);
  const [supportResolutionLoading, setSupportResolutionLoading] = useState(false);
  const [resolvedProductId, setResolvedProductId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!serial) return;
    setGradableModel(serial.gradableModel?._id ?? "");
    setProductId(originalProductId);
    setSerialNumber(serial.serialNumber ?? "");
    setGeneralScore(serial.generalScore ?? "");
    setSurface(serial.surface ?? "");
    setEdges(serial.edges ?? "");
    setCentering(serial.centering ?? "");
    setCorners(serial.corners ?? "");
    setNotaAutografo(serial.notaAutografo ?? "");
    setEnableChanges(false);
    setSupportsAutographNote(serial?.product?.autographEvaluation === true);
    setResolvedProductId(originalProductId);
  }, [serial, originalProductId]);

  const resolveSubgradesSupport = async (targetProductId) => {
    const normalizedProductId = normalizeProductId(targetProductId);
    const currentRequestId = ++supportRequestIdRef.current;

    if (!normalizedProductId) {
      if (currentRequestId !== supportRequestIdRef.current) return null;
      setSupportsGeneralScore(true);
      setSupportsSubgrades(true);
      setSupportsAutographNote(false);
      setNotaAutografo("");
      setResolvedProductId("");
      setSupportResolutionLoading(false);
      return { supportsGeneralScore: true, supportsSubgrades: true, supportsAutographNote: false };
    }

    setSupportResolutionLoading(true);
    const productResponse = await getProduct(user?.token, normalizedProductId);
    if (currentRequestId !== supportRequestIdRef.current) return null;

    if (productResponse?.type === "success") {
      const supportsGeneral = gradingTypeSupportsGeneralScore(
        productResponse?.data?.gradingType
      );
      const supportsSub = gradingTypeSupportsSubgrades(
        productResponse?.data?.gradingType,
        productResponse?.data?.preLacreFormat
      );
      const supportsAutograph = productResponse?.data?.autographEvaluation === true;
      setSupportsGeneralScore(supportsGeneral);
      setSupportsSubgrades(supportsSub);
      setSupportsAutographNote(supportsAutograph);
      if (!supportsAutograph) setNotaAutografo("");
      setResolvedProductId(normalizedProductId);
      setSupportResolutionLoading(false);
      return {
        supportsGeneralScore: supportsGeneral,
        supportsSubgrades: supportsSub,
        supportsAutographNote: supportsAutograph,
      };
    }

    setSupportResolutionLoading(false);
    badToast("Não foi possível validar o tipo do pedido para subgrades");
    return null;
  };

  useEffect(() => {
    if (!open || !serial) return;
    void resolveSubgradesSupport(productId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId, user?.token, serial]);

  const buildChanges = (supports) => {
    const changes = {};
    if (gradableModel !== serial.gradableModel?._id) {
      changes.gradableModel = gradableModel;
    }
    if (serialNumber !== serial.serialNumber) {
      changes.serialNumber = serialNumber;
    }
    if (supports?.supportsGeneralScore && generalScore !== serial.generalScore) {
      changes.generalScore = generalScore;
    }
    if (supports?.supportsSubgrades) {
      if (surface !== serial.surface) changes.surface = surface;
      if (edges !== serial.edges) changes.edges = edges;
      if (centering !== serial.centering) changes.centering = centering;
      if (corners !== serial.corners) changes.corners = corners;
    }
    if (
      supports?.supportsAutographNote &&
      notaAutografo !== (serial.notaAutografo || "")
    ) {
      changes.notaAutografo = notaAutografo;
    }
    if (productId !== originalProductId) {
      changes.productId = productId;
    }
    return changes;
  };

  const handleClose = () => {
    setEnableChanges(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!serial) return;
    if (supportResolutionLoading) {
      badToast("Aguarde a validação do tipo do pedido.");
      return;
    }

    let supports = { supportsGeneralScore, supportsSubgrades, supportsAutographNote };
    if (productId !== resolvedProductId) {
      supports = await resolveSubgradesSupport(productId);
    }
    if (supports === null) return;

    if (supports?.supportsAutographNote && !notaAutografo) {
      badToast("Selecione a nota do autógrafo.");
      return;
    }

    const body = buildChanges(supports);
    if (!Object.keys(body).length) {
      badToast("Nenhuma alteração para salvar");
      return;
    }

    setLoading(true);
    const data = await updateSerial(user.token, serial._id, body);
    setLoading(false);

    if (data.type === "success") {
      goodToast("Serial atualizado");
      handleClose();
      onChanged();
    }
    if (data.type === "error") {
      badToast(data.message);
    }
  };

  const navigateToProduct = () => {
    const targetId = normalizeProductId(productId);
    if (!targetId) return;
    navigate(`/manafix/products/${targetId}`);
    handleClose();
  };

  if (!serial) return null;

  const productRef = serial?.product || serial?.productData;
  const visibleGrades = getVisibleGradeFields(serial, productRef);

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Detalhe do serial">
      <div className="space-y-4">
        <MobileFormField label="ID">
          <input className={inputClass} value={serial._id} disabled aria-label="ID" />
        </MobileFormField>
        <MobileFormField label="ID do modelo">
          <input
            className={inputClass}
            value={gradableModel}
            onChange={(e) => setGradableModel(e.target.value)}
            disabled={!enableChanges}
            aria-label="ID do modelo"
          />
        </MobileFormField>
        <MobileFormField label="ID do pedido">
          <input
            className={inputClass}
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setResolvedProductId("");
            }}
            disabled={!enableChanges}
            aria-label="ID do pedido"
          />
        </MobileFormField>
        <MobileFormField label="Número de serial">
          <input
            className={inputClass}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            disabled={!enableChanges}
            aria-label="Número de serial"
          />
        </MobileFormField>

        {serial?.gradableModel ? (
          <div className="rounded-lg border border-primary/30 p-3">
            <h3 className="mb-2 text-sm font-semibold text-laranjinha">Modelo</h3>
            <SheetDetailRow label="Jogo" value={serial.gradableModel.game} />
            <SheetDetailRow label="Nome" value={serial.gradableModel.name} />
            <SheetDetailRow label="Edição" value={serial.gradableModel.edition} />
            <SheetDetailRow label="Ano" value={serial.gradableModel.year} />
          </div>
        ) : null}

        {enableChanges ? (
          <>
            {supportsGeneralScore ? (
              <MobileFormField label="Nota geral">
                <input
                  className={inputClass}
                  value={generalScore}
                  onChange={(e) => setGeneralScore(e.target.value)}
                  aria-label="Nota geral"
                />
              </MobileFormField>
            ) : null}
            {supportsSubgrades ? (
              <>
                <MobileFormField label="Surface">
                  <input
                    className={inputClass}
                    value={surface}
                    onChange={(e) => setSurface(e.target.value)}
                    aria-label="Surface"
                  />
                </MobileFormField>
                <MobileFormField label="Edges">
                  <input
                    className={inputClass}
                    value={edges}
                    onChange={(e) => setEdges(e.target.value)}
                    aria-label="Edges"
                  />
                </MobileFormField>
                <MobileFormField label="Centering">
                  <input
                    className={inputClass}
                    value={centering}
                    onChange={(e) => setCentering(e.target.value)}
                    aria-label="Centering"
                  />
                </MobileFormField>
                <MobileFormField label="Corners">
                  <input
                    className={inputClass}
                    value={corners}
                    onChange={(e) => setCorners(e.target.value)}
                    aria-label="Corners"
                  />
                </MobileFormField>
              </>
            ) : null}
            {supportsAutographNote ? (
              <MobileFormField label="Nota do autógrafo">
                <select
                  className={inputClass}
                  value={notaAutografo}
                  onChange={(e) => setNotaAutografo(e.target.value)}
                  aria-label="Nota do autógrafo"
                >
                  <option value="">Selecione</option>
                  <option value="Autentico">Autentico</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </select>
              </MobileFormField>
            ) : null}
          </>
        ) : (
          <div className="rounded-lg border border-primary/30 p-3">
            <h3 className="mb-2 text-sm font-semibold text-laranjinha">Notas</h3>
            {visibleGrades.generalScore ? (
              <SheetDetailRow
                label={visibleGrades.generalScore.label}
                value={renderApplicableGradeValue(visibleGrades.generalScore.value)}
              />
            ) : null}
            {visibleGrades.subgrades.map((field) => (
              <SheetDetailRow
                key={field.key}
                label={field.label}
                value={renderApplicableGradeValue(field.value)}
              />
            ))}
            {visibleGrades.autographNote ? (
              <SheetDetailRow
                label={visibleGrades.autographNote.label}
                value={renderApplicableGradeValue(visibleGrades.autographNote.value)}
              />
            ) : null}
          </div>
        )}

        {productId ? (
          <button type="button" onClick={navigateToProduct} className={primaryBtn}>
            Ver pedido
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => setEnableChanges((current) => !current)}
          className={secondaryBtn}
        >
          {enableChanges ? "Bloquear alterações" : "Liberar alterações"}
        </button>

        <div className="flex gap-2">
          <button type="button" onClick={handleClose} className={dangerBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!enableChanges || supportResolutionLoading || loading}
            className={primaryBtn}
          >
            {loading ? "Salvando…" : "Atualizar"}
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}
